import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../App';
import { useCurrency } from '../contexts/CurrencyContext';
import Navbar from '../components/Navbar';
import { flag } from '../utils/countryFlags';
import { normalize } from '../utils/textUtils';
import GlobalNavigation from '../components/GlobalNavigation';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import {
  ShoppingCart, Clock, CheckCircle, DollarSign, Star, Filter, MapPin, AlertTriangle, History,
  Wallet, Eye, Package, XCircle, CreditCard, Mail, Pencil, Trash2, ExternalLink, Shield,
  Search, ArrowRight, TrendingUp, Zap, Plane, ShieldCheck, Headphones, MessageSquare
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import OrderDetailModal from '../components/OrderDetailModal';
import SupportContact from '../components/SupportContact';

// ─── HELPERS ─────────────────────────────────────────────────────────
function getMinPrice(listing) {
  if (listing?.baggage_items?.length) {
    const prices = listing.baggage_items
      .filter(i => i.price_per_unit > 0)
      .map(i => i.price_per_unit);
    if (prices.length) return Math.min(...prices);
  }
  return listing?.price_per_unit ?? 0;
}

function getUnitLabel(listing) {
  if (listing?.baggage_items?.length) {
    const kilos = listing.baggage_items.find(i => i.type === 'kilos');
    if (kilos) return 'kg';
    return listing.baggage_items[0]?.type === 'sac_23kg' ? 'sac 23kg' : listing.baggage_items[0]?.type || '';
  }
  const t = listing?.baggage_type;
  return t === 'kilos' ? 'kg' : t === 'enveloppes' ? 'env.' : t === 'sac_23kg' ? 'sac 23kg' : (t || '');
}

// ─── CONSTANTS ───────────────────────────────────────────────────────
const statusLabels = {
  pending_admin_validation: 'En attente de validation',
  pending_payment: 'En attente de paiement',
  paid: 'Paiement confirmé',
  awaiting_pickup_seller: 'En attente récupération',
  received_admin_departure: 'Reçu Admin - Départ',
  in_transit: 'En transit',
  received_admin_destination: 'Reçu Admin - Destination',
  delivered: 'Livré - À confirmer',
  completed: 'Complété',
  cancelled: 'Annulé',
  rejected: 'Refusée',
  refunded: 'Remboursé',
  disputed: 'En litige'
};

const statusColors = {
  pending_admin_validation: 'bg-amber-500/20 text-amber-600',
  pending_payment: 'bg-gray-500/20 text-gray-500',
  paid: 'bg-amber-500/20 text-amber-500',
  awaiting_pickup_seller: 'bg-orange-500/20 text-orange-500',
  received_admin_departure: 'bg-teal-500/20 text-teal-500',
  in_transit: 'bg-violet-500/20 text-violet-500',
  received_admin_destination: 'bg-sky-500/20 text-sky-500',
  delivered: 'bg-green-500/20 text-green-500',
  completed: 'bg-emerald-500/20 text-emerald-500',
  cancelled: 'bg-red-500/20 text-red-500',
  rejected: 'bg-red-500/20 text-red-500',
  refunded: 'bg-amber-500/20 text-amber-500',
  disputed: 'bg-red-500/20 text-red-500'
};

const deliveryDelayLabels = { same_day: 'Le même jour', '1_day': '1 jour après', '2_days': '2 jours après' };
const TYPE_LABELS_SHORT = { kilos: 'kg', sacs: 'sac', enveloppes: 'env.', telephones: 'tél.', ordinateurs: 'ordi.', sac_23kg: 'sac 23kg' };
const countries = ['France', 'Sénégal', 'Côte d\'Ivoire', 'Cameroun', 'Maroc', 'Canada', 'États-Unis', 'Belgique', 'Suisse', 'Allemagne'];

function formatOrderBaggage(order) {
  if (order.selected_items && order.selected_items.length > 0) {
    return order.selected_items.map(it => it.quantity + ' ' + (TYPE_LABELS_SHORT[it.type] || it.type)).join(', ');
  }
  if (order.baggage_type) return order.quantity + ' ' + (TYPE_LABELS_SHORT[order.baggage_type] || order.baggage_type);
  return order.quantity + ' unité(s)';
}

function CountdownTimer({ deadline }) {
  const [remaining, setRemaining] = React.useState('');
  React.useEffect(() => {
    const update = () => {
      const now = new Date();
      const dl = new Date(deadline);
      const diff = dl - now;
      if (diff <= 0) { setRemaining('Expiré'); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      setRemaining(`${h}h ${m}min restantes`);
    };
    update();
    const interval = setInterval(update, 60000);
    return () => clearInterval(interval);
  }, [deadline]);
  const isExpired = remaining === 'Expiré';
  return <span className={`text-xs ${isExpired ? 'text-red-500' : 'text-amber-600'}`}>{remaining}</span>;
}

// ─── MAIN COMPONENT ──────────────────────────────────────────────────
const BuyerDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token, API } = useAuth();
  const { formatPrice } = useCurrency();

  const urlTab = searchParams.get('tab') || 'orders';
  const highlightOrderId = searchParams.get('order');

  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [activeTab, setActiveTab] = useState(urlTab);
  const [historyOrders, setHistoryOrders] = useState([]);
  const [cancelledOrders, setCancelledOrders] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recommendedListings, setRecommendedListings] = useState([]);
  const [filters, setFilters] = useState({ status: '', departureCountry: '', destinationCountry: '', departure: '', destination: '', travelDate: '' });
  const [disputeDialog, setDisputeDialog] = useState({ open: false, orderId: null });
  const [disputeForm, setDisputeForm] = useState({ reason: '', description: '' });
  const [viewingOrderDetail, setViewingOrderDetail] = useState(null);
  const [cancelDialog, setCancelDialog] = useState({ open: false, order: null, fee: 0, feeRate: 0, loading: false });
  const [modifyDialog, setModifyDialog] = useState({ open: false, order: null, loading: false });

  // Search state
  const [searchDep, setSearchDep] = useState('');
  const [searchDest, setSearchDest] = useState('');
  const [searchDate, setSearchDate] = useState('');

  useEffect(() => {
    if (highlightOrderId && orders.length > 0) {
      const element = document.getElementById(`order-${highlightOrderId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.classList.add('ring-2', 'ring-[#D4AF37]', 'ring-offset-2');
        setTimeout(() => element.classList.remove('ring-2', 'ring-[#D4AF37]', 'ring-offset-2'), 3000);
      }
    }
  }, [highlightOrderId, orders]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [statsRes, ordersRes, listingsRes] = await Promise.all([
        axios.get(`${API}/buyer/stats`, { headers }),
        axios.get(`${API}/buyer/orders`, { headers }),
        axios.get(`${API}/listings?status=active&limit=3`, { headers }).catch(() => ({ data: [] }))
      ]);
      setStats(statsRes.data);
      setRecommendedListings(Array.isArray(listingsRes.data) ? listingsRes.data.slice(0, 3) : []);

      const activeStatuses = ['pending_admin_validation', 'pending_payment', 'paid', 'awaiting_pickup_seller', 'received_admin_departure', 'in_transit', 'received_admin_destination', 'delivered'];
      const allOrdersData = ordersRes.data;
      setOrders(allOrdersData.filter(o => activeStatuses.includes(o.current_status) && !(o.current_status === 'delivered' && o.buyer_confirmed_delivered)));
      setHistoryOrders(allOrdersData.filter(o => o.current_status === 'completed' || (o.current_status === 'delivered' && o.buyer_confirmed_delivered)));
      setCancelledOrders(allOrdersData.filter(o => ['cancelled', 'refunded', 'rejected'].includes(o.current_status)));
      setAllOrders(allOrdersData);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const confirmStatus = async (orderId, confirmed) => {
    try {
      if (!confirmed) { setDisputeDialog({ open: true, orderId }); return; }
      const response = await axios.post(`${API}/orders/${orderId}/confirm`, { confirmed: true }, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.escrow_released) {
        toast.success('Livraison confirmée ! Le paiement a été libéré au vendeur.');
      } else { toast.success('Statut confirmé !'); }
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur'); }
  };

  const submitDispute = async () => {
    try {
      await axios.post(`${API}/disputes`, { order_id: disputeDialog.orderId, reason: disputeForm.reason, description: disputeForm.description }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Litige ouvert. Un administrateur va examiner votre cas.');
      setDisputeDialog({ open: false, orderId: null });
      setDisputeForm({ reason: '', description: '' });
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur'); }
  };

  const filterOrders = (orderList) => {
    return orderList.filter(o => {
      if (filters.status && filters.status !== 'all' && o.current_status !== filters.status) return false;
      if (filters.departureCountry && filters.departureCountry !== 'all' && o.departure_country !== filters.departureCountry) return false;
      if (filters.destinationCountry && filters.destinationCountry !== 'all' && o.destination_country !== filters.destinationCountry) return false;
      if (filters.departure && !normalize(o.departure_city).includes(normalize(filters.departure))) return false;
      if (filters.destination && !normalize(o.destination_city).includes(normalize(filters.destination))) return false;
      if (filters.travelDate && o.travel_date !== filters.travelDate) return false;
      return true;
    });
  };

  const filteredOrders = filterOrders(orders);
  const filteredHistory = filterOrders(historyOrders);
  const filteredCancelled = filterOrders(cancelledOrders);
  const filteredAll = filterOrders(allOrders);

  const calculateCancelFee = (order) => {
    if (order.current_status === 'pending_admin_validation') return { fee: 0, feeRate: 0 };
    const travelDate = order.travel_date ? new Date(order.travel_date + 'T00:00:00Z') : null;
    if (!travelDate) return { fee: 0, feeRate: 0 };
    const daysUntilTravel = Math.floor((travelDate - new Date()) / (1000 * 60 * 60 * 24));
    if (daysUntilTravel < 2) return { fee: order.total * 0.50, feeRate: 50 };
    if (daysUntilTravel < 7) return { fee: order.total * 0.25, feeRate: 25 };
    return { fee: 0, feeRate: 0 };
  };

  const openCancelDialog = (order) => {
    const { fee, feeRate } = calculateCancelFee(order);
    setCancelDialog({ open: true, order, fee, feeRate, loading: false });
  };

  const confirmCancel = async () => {
    if (!cancelDialog.order) return;
    setCancelDialog(prev => ({ ...prev, loading: true }));
    try {
      await axios.post(`${API}/orders/${cancelDialog.order.id}/buyer-cancel`, { accepted: true }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Commande annulée avec succès');
      setCancelDialog({ open: false, order: null, fee: 0, feeRate: 0, loading: false });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'annulation');
      setCancelDialog(prev => ({ ...prev, loading: false }));
    }
  };

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (searchDep) params.set('departure', searchDep);
    if (searchDest) params.set('destination', searchDest);
    if (searchDate) params.set('date', searchDate);
    navigate(`/listings${params.toString() ? '?' + params.toString() : ''}`);
  };

  // Popular destinations from orders
  const destCounts = {};
  allOrders.forEach(o => { const d = o.destination_country; if (d) destCounts[d] = (destCounts[d] || 0) + 1; });
  const topDests = Object.entries(destCounts).sort((a, b) => b[1] - a[1]).slice(0, 3);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center py-32">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D4AF37]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="buyer-dashboard">
      <Navbar />
      <GlobalNavigation />

      {/* ── HERO ── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-background via-muted to-background" />
        <div className="absolute top-[-80px] left-[-40px] w-[400px] h-[400px] rounded-full bg-[#D4AF37]/[0.04] blur-[100px]" />
        <div className="absolute bottom-[-60px] right-[20%] w-[300px] h-[300px] rounded-full bg-emerald-500/[0.03] blur-[80px]" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-6">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div>
              <p className="text-[#D4AF37]/70 text-xs tracking-[0.2em] uppercase mb-2">Espace Acheteur</p>
              <h1 className="font-heading text-2xl sm:text-3xl font-bold text-foreground mb-2" data-testid="buyer-hero-title">
                Trouvez un transport rapidement
              </h1>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <Package className="w-4 h-4 text-emerald-400" />
                  <strong className="text-foreground">{recommendedListings.length}</strong> offres disponibles
                </span>
                {topDests.length > 0 && (
                  <span className="flex items-center gap-1.5">
                    <MapPin className="w-4 h-4 text-[#D4AF37]" />
                    Top : {topDests.map(([c], i) => <span key={c}>{i > 0 && ', '}{flag(c)} {c}</span>)}
                  </span>
                )}
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => navigate('/listings')}
                className="bg-muted/40 border-border text-muted-foreground hover:text-foreground rounded-xl"
                data-testid="search-now-btn"
              >
                <Search className="w-4 h-4 mr-2" /> Rechercher maintenant
              </Button>
              <Button
                onClick={() => setActiveTab('orders')}
                className="bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold rounded-xl"
                data-testid="my-orders-btn"
              >
                <ShoppingCart className="w-4 h-4 mr-2" /> Mes commandes
              </Button>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">

        {/* ── SEARCH BAR ── */}
        <div className="bg-muted/30 border border-border rounded-xl p-4 mb-6" data-testid="search-bar">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div>
              <label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1 block">Départ</label>
              <Input
                placeholder="Ville ou pays de départ"
                value={searchDep}
                onChange={e => setSearchDep(e.target.value)}
                className="bg-muted/40 border-border text-foreground placeholder:text-foreground/30"
                data-testid="search-departure"
              />
            </div>
            <div>
              <label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1 block">Destination</label>
              <Input
                placeholder="Ville ou pays de destination"
                value={searchDest}
                onChange={e => setSearchDest(e.target.value)}
                className="bg-muted/40 border-border text-foreground placeholder:text-foreground/30"
                data-testid="search-destination"
              />
            </div>
            <div>
              <label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1 block">Date</label>
              <Input
                type="date"
                value={searchDate}
                onChange={e => setSearchDate(e.target.value)}
                className="bg-muted/40 border-border text-foreground"
                data-testid="search-date"
              />
            </div>
            <div className="flex items-end">
              <Button
                className="w-full bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold rounded-xl h-10"
                onClick={handleSearch}
                data-testid="search-btn"
              >
                <Search className="w-4 h-4 mr-2" /> Rechercher
              </Button>
            </div>
          </div>
        </div>

        {/* ── STATS GRID ── */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="buyer-stats-grid">
          {[
            { icon: ShoppingCart, label: 'Total', value: stats?.total_orders || 0, accent: 'text-[#D4AF37]', onClick: () => setActiveTab('all') },
            { icon: Clock, label: 'En cours', value: stats?.active_orders || 0, accent: 'text-orange-400', onClick: () => setActiveTab('orders') },
            { icon: CheckCircle, label: 'Complétées', value: stats?.completed_orders || 0, accent: 'text-green-400', onClick: () => setActiveTab('history') },
            { icon: XCircle, label: 'Annulées', value: cancelledOrders.length, accent: 'text-red-400', onClick: () => setActiveTab('cancelled') },
            { icon: DollarSign, label: 'Total dépensé', value: formatPrice(stats?.total_spent || 0), accent: 'text-[#D4AF37]' },
            { icon: Star, label: 'Ma note', value: stats?.rating?.toFixed(1) || '0.0', accent: 'text-yellow-400' },
          ].map(s => (
            <button
              key={s.label}
              className={`bg-muted/30 border border-border rounded-xl p-4 text-left transition-all hover:border-white/[0.12] ${s.onClick ? 'cursor-pointer' : ''}`}
              onClick={s.onClick}
              data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}
            >
              <s.icon className={`w-6 h-6 ${s.accent} mb-2`} />
              <p className="text-xl font-bold text-foreground">{s.value}</p>
              <p className="text-[11px] text-muted-foreground uppercase tracking-wider">{s.label}</p>
              {s.onClick && <p className="text-[10px] text-[#D4AF37]/60 mt-1">Cliquer pour voir</p>}
            </button>
          ))}
        </div>

        {/* ── OFFRES RECOMMANDÉES ── */}
        {recommendedListings.length > 0 && (
          <div className="mb-8" data-testid="recommended-section">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-[#D4AF37]" />
                <h2 className="font-heading text-lg font-bold text-foreground">Offres recommandées</h2>
              </div>
              <Button variant="link" className="text-[#D4AF37] text-sm p-0" onClick={() => navigate('/listings')}>
                Voir toutes <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {recommendedListings.map(listing => {
                const fillRate = listing.quantity > 0 ? Math.round(((listing.quantity - (listing.available_quantity ?? listing.quantity)) / listing.quantity) * 100) : 0;
                const isPopular = fillRate >= 50 || (listing.orders_active || 0) >= 2;
                const isUrgent = listing.travel_date && ((new Date(listing.travel_date) - new Date()) / (1000 * 60 * 60 * 24)) <= 5;
                return (
                  <div key={listing.id} className="bg-muted/30 border border-border rounded-xl p-4 hover:border-[#D4AF37]/30 transition-all group" data-testid={`recommended-${listing.id}`}>
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      {isPopular && <Badge className="text-[9px] bg-emerald-500/15 text-emerald-400 border-emerald-500/30">Populaire</Badge>}
                      {isUrgent && <Badge className="text-[9px] bg-red-500/15 text-red-400 border-red-500/30">Urgent</Badge>}
                      <span className="text-[10px] text-foreground/30 ml-auto">{listing.airline}</span>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground mb-1">
                      {flag(listing.departure_country)} {listing.departure_city} → {flag(listing.destination_country)} {listing.destination_city}
                    </h3>
                    <p className="text-xs text-muted-foreground mb-2">
                      {new Date(listing.travel_date + 'T12:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                    </p>
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-[9px] text-foreground/30 uppercase tracking-wider leading-none mb-0.5">À partir de</p>
                        <div className="flex items-baseline gap-1">
                          <span className="text-lg font-bold text-[#D4AF37]">{formatPrice(getMinPrice(listing))}</span>
                          <span className="text-[10px] text-muted-foreground">/{getUnitLabel(listing)}</span>
                        </div>
                      </div>
                      {listing.seller_rating > 0 && (
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />{listing.seller_rating?.toFixed(1)}
                        </span>
                      )}
                    </div>
                    {/* Fill rate indicator */}
                    <div className="mb-3">
                      <div className="flex justify-between text-[10px] text-foreground/30 mb-1">
                        <span>{listing.available_quantity ?? listing.quantity} places restantes</span>
                        <span>{fillRate}% rempli</span>
                      </div>
                      <div className="h-1 bg-muted/50 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${fillRate >= 70 ? 'bg-red-500' : fillRate >= 30 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${Math.max(fillRate, 5)}%` }} />
                      </div>
                    </div>
                    <Button
                      className="w-full bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold rounded-lg text-sm h-9"
                      onClick={() => navigate(`/listings/${listing.id}`)}
                      data-testid={`reserve-${listing.id}`}
                    >
                      Réserver maintenant <ArrowRight className="w-3 h-3 ml-1" />
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── MAIN TABS ── */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 bg-muted/40 border border-border">
            <TabsTrigger value="orders"><Package className="w-4 h-4 mr-2" />Mes Commandes ({orders.length})</TabsTrigger>
            <TabsTrigger value="all" data-testid="tab-all"><ShoppingCart className="w-4 h-4 mr-2" />Toutes ({allOrders.length})</TabsTrigger>
            <TabsTrigger value="history"><History className="w-4 h-4 mr-2" />Historique</TabsTrigger>
            <TabsTrigger value="cancelled" data-testid="tab-cancelled"><XCircle className="w-4 h-4 mr-2" />Annulées ({cancelledOrders.length})</TabsTrigger>
          </TabsList>

          {/* ═══ ORDERS TAB ═══ */}
          <TabsContent value="orders">
            <Card className="mb-6 bg-muted/30 border-border">
              <CardContent className="py-4">
                <div className="flex items-center gap-2 mb-3"><Filter className="w-4 h-4 text-muted-foreground" /><span className="font-medium text-foreground/70">Filtres</span></div>
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                  <Select value={filters.status} onValueChange={v => setFilters({...filters, status: v})}>
                    <SelectTrigger><SelectValue placeholder="Statut" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous</SelectItem>
                      {Object.entries(statusLabels).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
                    </SelectContent>
                  </Select>
                  <Select value={filters.departureCountry} onValueChange={v => setFilters({...filters, departureCountry: v})}>
                    <SelectTrigger><SelectValue placeholder="Pays départ" /></SelectTrigger>
                    <SelectContent><SelectItem value="all">Tous pays</SelectItem>{countries.map(c => <SelectItem key={c} value={c} textValue={c}>{flag(c)} {c}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={filters.destinationCountry} onValueChange={v => setFilters({...filters, destinationCountry: v})}>
                    <SelectTrigger><SelectValue placeholder="Pays arrivée" /></SelectTrigger>
                    <SelectContent><SelectItem value="all">Tous pays</SelectItem>{countries.map(c => <SelectItem key={c} value={c} textValue={c}>{flag(c)} {c}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input placeholder="Ville départ" value={filters.departure} onChange={e => setFilters({...filters, departure: e.target.value})} />
                  <Input placeholder="Ville arrivée" value={filters.destination} onChange={e => setFilters({...filters, destination: e.target.value})} />
                  <Input type="date" value={filters.travelDate} onChange={e => setFilters({...filters, travelDate: e.target.value})} />
                </div>
                {(filters.status || filters.departureCountry || filters.destinationCountry || filters.departure || filters.destination || filters.travelDate) && (
                  <Button variant="ghost" size="sm" className="mt-2 text-muted-foreground" onClick={() => setFilters({ status: '', departureCountry: '', destinationCountry: '', departure: '', destination: '', travelDate: '' })}>Réinitialiser les filtres</Button>
                )}
              </CardContent>
            </Card>

            <div className="space-y-4">
              {filteredOrders.length === 0 ? (
                <div className="bg-muted/30 border border-border rounded-xl py-12 text-center">
                  <ShoppingCart className="w-12 h-12 mx-auto mb-4 text-foreground/20" />
                  <p className="text-muted-foreground mb-4">Aucune commande en cours</p>
                  <Button onClick={() => navigate('/listings')} className="bg-[#D4AF37] hover:bg-[#C4A030] text-black rounded-xl">Parcourir les annonces</Button>
                </div>
              ) : (
                filteredOrders.map(order => (
                  <Card key={order.id} id={`order-${order.id}`} className="bg-muted/30 border-border hover:border-white/[0.12] transition-all">
                    <CardContent className="py-4">
                      <div className="flex flex-col lg:flex-row gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <Badge className={statusColors[order.current_status]}>{statusLabels[order.current_status]}</Badge>
                            <span className="text-sm text-muted-foreground">Vol du {new Date(order.travel_date + 'T12:00:00').toLocaleDateString('fr-FR')}</span>
                          </div>
                          <h3 className="font-medium text-lg text-foreground mb-1">
                            {flag(order.departure_country)} {order.departure_city} → {flag(order.destination_country)} {order.destination_city}
                          </h3>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                            <span className="flex items-center gap-1"><Star className="w-3 h-3 text-yellow-500" />Vendeur: {order.seller_rating?.toFixed(1) || 'N/A'}</span>
                            <span>{formatOrderBaggage(order)}</span>
                            <span className="font-medium text-foreground">{formatPrice(order.total)}</span>
                            {order.insurance_cost > 0 && <span className="text-xs text-primary">(dont {formatPrice(order.insurance_cost)} assurance)</span>}
                          </div>
                          {order.listing_id && (
                            <Link to={`/listings/${order.listing_id}`} className="text-[#D4AF37] hover:underline text-xs inline-flex items-center gap-1 mt-2"><ExternalLink className="w-3 h-3" />Voir l'annonce</Link>
                          )}
                          {order.pickup_cities?.length > 0 && (
                            <div className="mt-3 p-3 bg-muted/40 rounded-lg text-sm space-y-1">
                              <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-green-500" /><span className="text-muted-foreground">Ramassage: {order.pickup_cities.join(' ou ')}</span></div>
                              <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-[#D4AF37]" /><span className="text-muted-foreground">Récupération: {order.delivery_cities?.join(' ou ')}</span></div>
                              <div className="flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-orange-500" /><span className="text-muted-foreground">Délai: {deliveryDelayLabels[order.delivery_delay] || order.delivery_delay}</span></div>
                            </div>
                          )}
                        </div>

                        {/* Tracking */}
                        <div className="lg:w-64 border-t lg:border-t-0 lg:border-l border-border pt-4 lg:pt-0 lg:pl-4">
                          <p className="text-xs text-foreground/30 mb-2">Suivi de commande</p>
                          <div className="space-y-2">
                            {['paid', 'awaiting_pickup_seller', 'received_admin_departure', 'in_transit', 'received_admin_destination', 'delivered'].map(s => {
                              const statusOrder = { pending_payment: 0, paid: 1, awaiting_pickup_seller: 2, received_admin_departure: 3, in_transit: 4, received_admin_destination: 5, delivered: 6 };
                              const currentOrder = statusOrder[order.current_status] ?? 0;
                              const thisOrder = statusOrder[s];
                              const isActive = thisOrder <= currentOrder;
                              const isCurrent = s === order.current_status;
                              return (
                                <div key={s} className={`flex items-center gap-2 text-xs ${isActive ? 'text-foreground/70' : 'text-foreground/20'}`}>
                                  <div className={`w-2.5 h-2.5 rounded-full ${isCurrent ? 'bg-[#D4AF37] animate-pulse' : isActive ? 'bg-green-500' : 'bg-muted'}`} />
                                  <span className={isCurrent ? 'font-medium' : ''}>{statusLabels[s]}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>

                      {/* Delivery confirmation */}
                      {order.current_status === 'delivered' && !order.buyer_confirmed_delivered && (
                        <div className="flex items-center gap-4 p-3 bg-green-500/10 border border-green-500/20 rounded-lg mt-4">
                          <p className="text-sm text-foreground/70 flex-1">Le colis a été livré. Confirmez la réception pour libérer le paiement au vendeur.</p>
                          <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => confirmStatus(order.id, true)}>Confirmer</Button>
                          <Button size="sm" variant="destructive" onClick={() => confirmStatus(order.id, false)}>Contester</Button>
                        </div>
                      )}

                      {order.dispute_id && (
                        <Badge className="bg-red-500/20 text-red-500 w-fit mt-2"><AlertTriangle className="w-3 h-3 mr-1" />Litige en cours</Badge>
                      )}

                      {/* Actions */}
                      <div className="mt-3 flex justify-end gap-2 flex-wrap">
                        {['pending_admin_validation', 'pending_payment', 'paid', 'awaiting_pickup_seller'].includes(order.current_status) && (
                          <Button variant="destructive" size="sm" onClick={() => openCancelDialog(order)} data-testid={`buyer-cancel-order-${order.id}`}>
                            <Trash2 className="w-3.5 h-3.5 mr-1" />Annuler
                          </Button>
                        )}
                        {order.current_status === 'pending_admin_validation' && (
                          <Button variant="outline" size="sm" className="bg-muted/40 border-border text-muted-foreground" onClick={() => setModifyDialog({ open: true, order, loading: false })} data-testid={`buyer-modify-order-${order.id}`}>
                            <Pencil className="w-3.5 h-3.5 mr-1" />Modifier
                          </Button>
                        )}
                        <SupportContact orderId={order.id} triggerButton={
                          <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground" data-testid={`buyer-contact-support-${order.id}`}><Mail className="w-3.5 h-3.5 mr-1" />Support</Button>
                        } />
                        <Button variant="outline" size="sm" className="bg-muted/40 border-border text-muted-foreground" onClick={() => setViewingOrderDetail(order)} data-testid={`view-order-detail-${order.id}`}>
                          <Eye className="w-3.5 h-3.5 mr-1" />Détails
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* ═══ HISTORY TAB ═══ */}
          <TabsContent value="history">
            <div className="space-y-4">
              {filteredHistory.length === 0 ? (
                <div className="bg-muted/30 border border-border rounded-xl py-12 text-center">
                  <History className="w-12 h-12 mx-auto mb-4 text-foreground/20" /><p className="text-muted-foreground">Aucun historique</p>
                </div>
              ) : (
                filteredHistory.map(order => (
                  <Card key={order.id} className="bg-muted/30 border-border opacity-80 hover:opacity-100 transition-opacity">
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className={statusColors[order.current_status]}>{statusLabels[order.current_status]}</Badge>
                            <span className="text-sm text-muted-foreground">{new Date(order.created_at).toLocaleDateString('fr-FR')}</span>
                          </div>
                          <h3 className="font-medium text-foreground">{flag(order.departure_country)} {order.departure_city} → {flag(order.destination_country)} {order.destination_city}</h3>
                          <p className="text-sm text-muted-foreground"><Star className="w-3 h-3 inline text-yellow-500 mr-1" />Vendeur: {order.seller_rating?.toFixed(1) || 'N/A'} {formatOrderBaggage(order)}</p>
                        </div>
                        <div className="text-right flex flex-col items-end gap-2">
                          <p className="text-xl font-bold text-foreground">{formatPrice(order.total)}</p>
                          <Button variant="outline" size="sm" className="bg-muted/40 border-border text-muted-foreground" onClick={() => setViewingOrderDetail(order)} data-testid={`view-history-detail-${order.id}`}><Eye className="w-3.5 h-3.5 mr-1" />Détails</Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* ═══ CANCELLED TAB ═══ */}
          <TabsContent value="cancelled" data-testid="cancelled-orders-tab">
            <div className="mb-6 bg-red-500/5 border border-red-500/20 rounded-xl p-4 flex items-center gap-3">
              <XCircle className="w-6 h-6 text-red-400" /><div><p className="font-medium text-foreground">Commandes annulées ou remboursées</p><p className="text-sm text-muted-foreground">{cancelledOrders.length} commande(s)</p></div>
            </div>
            <div className="space-y-4">
              {filteredCancelled.length === 0 ? (
                <div className="bg-muted/30 border border-border rounded-xl py-12 text-center"><XCircle className="w-12 h-12 mx-auto mb-4 text-foreground/20" /><p className="text-muted-foreground">Aucune commande annulée</p></div>
              ) : (
                filteredCancelled.map(order => (
                  <Card key={order.id} className="bg-muted/30 border-red-500/10 hover:border-red-500/30 transition-colors">
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1"><Badge className={statusColors[order.current_status]}>{statusLabels[order.current_status]}</Badge><span className="text-sm text-muted-foreground">{new Date(order.created_at).toLocaleDateString('fr-FR')}</span></div>
                          <h3 className="font-medium text-foreground">{flag(order.departure_country)} {order.departure_city} → {flag(order.destination_country)} {order.destination_city}</h3>
                          {order.cancellation_reason && <p className="text-sm text-red-400 mt-1">Raison: {order.cancellation_reason}</p>}
                        </div>
                        <div className="text-right flex flex-col items-end gap-2">
                          <p className="text-xl font-bold text-red-400 line-through">{formatPrice(order.total)}</p>
                          {order.current_status === 'refunded' && <Badge className="bg-amber-500/20 text-amber-400">Remboursé</Badge>}
                          <Button variant="outline" size="sm" className="bg-muted/40 border-border text-muted-foreground" onClick={() => setViewingOrderDetail(order)} data-testid={`view-cancelled-detail-${order.id}`}><Eye className="w-3.5 h-3.5 mr-1" />Détails</Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* ═══ ALL ORDERS TAB ═══ */}
          <TabsContent value="all" data-testid="all-orders-tab">
            <Card className="mb-6 bg-muted/30 border-border">
              <CardContent className="py-4">
                <div className="flex items-center gap-2 mb-3"><Filter className="w-4 h-4 text-muted-foreground" /><span className="font-medium text-foreground/70">Filtres</span></div>
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                  <Select value={filters.status} onValueChange={v => setFilters({...filters, status: v})}>
                    <SelectTrigger><SelectValue placeholder="Statut" /></SelectTrigger>
                    <SelectContent><SelectItem value="all">Tous</SelectItem>{Object.entries(statusLabels).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}</SelectContent>
                  </Select>
                  <Select value={filters.departureCountry} onValueChange={v => setFilters({...filters, departureCountry: v})}>
                    <SelectTrigger><SelectValue placeholder="Pays départ" /></SelectTrigger>
                    <SelectContent><SelectItem value="all">Tous pays</SelectItem>{countries.map(c => <SelectItem key={c} value={c} textValue={c}>{flag(c)} {c}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={filters.destinationCountry} onValueChange={v => setFilters({...filters, destinationCountry: v})}>
                    <SelectTrigger><SelectValue placeholder="Pays arrivée" /></SelectTrigger>
                    <SelectContent><SelectItem value="all">Tous pays</SelectItem>{countries.map(c => <SelectItem key={c} value={c} textValue={c}>{flag(c)} {c}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input placeholder="Ville départ" value={filters.departure} onChange={e => setFilters({...filters, departure: e.target.value})} />
                  <Input placeholder="Ville arrivée" value={filters.destination} onChange={e => setFilters({...filters, destination: e.target.value})} />
                  <Input type="date" value={filters.travelDate} onChange={e => setFilters({...filters, travelDate: e.target.value})} />
                </div>
                {(filters.status || filters.departureCountry || filters.destinationCountry || filters.departure || filters.destination || filters.travelDate) && (
                  <Button variant="ghost" size="sm" className="mt-2 text-muted-foreground" onClick={() => setFilters({ status: '', departureCountry: '', destinationCountry: '', departure: '', destination: '', travelDate: '' })}>Réinitialiser</Button>
                )}
              </CardContent>
            </Card>
            <div className="space-y-4">
              {filteredAll.length === 0 ? (
                <div className="bg-muted/30 border border-border rounded-xl py-12 text-center"><ShoppingCart className="w-12 h-12 mx-auto mb-4 text-foreground/20" /><p className="text-muted-foreground mb-4">Aucune commande</p>
                  <Button onClick={() => navigate('/listings')} className="bg-[#D4AF37] hover:bg-[#C4A030] text-black rounded-xl">Parcourir les annonces</Button>
                </div>
              ) : (
                filteredAll.map(order => (
                  <Card key={order.id} className={`bg-muted/30 border-border hover:border-white/[0.12] transition-all ${order.current_status === 'cancelled' || order.current_status === 'refunded' ? 'border-red-500/10' : order.current_status === 'completed' ? 'border-emerald-500/10' : ''}`}>
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <Badge className={statusColors[order.current_status]}>{statusLabels[order.current_status]}</Badge>
                            <span className="text-sm text-muted-foreground">{new Date(order.created_at).toLocaleDateString('fr-FR')}</span>
                            {order.payment_method && order.payment_method !== 'card' && (
                              <Badge variant="outline" className="text-[10px] border-border text-muted-foreground">{order.payment_method === 'interac' ? 'Interac' : order.payment_method === 'orange_money' ? 'Orange Money' : order.payment_method === 'cash' ? 'Espèces' : order.payment_method}</Badge>
                            )}
                          </div>
                          <h3 className="font-medium text-foreground">{flag(order.departure_country)} {order.departure_city} → {flag(order.destination_country)} {order.destination_city}</h3>
                          <p className="text-sm text-muted-foreground">Vendeur: <span className="font-mono">KK-{(order.seller_id || '').slice(0, 8).toUpperCase()}</span> {formatOrderBaggage(order)}</p>
                          {order.listing_id && <Link to={`/listings/${order.listing_id}`} className="text-[#D4AF37] hover:underline text-xs inline-flex items-center gap-1 mt-0.5"><ExternalLink className="w-3 h-3" />Voir l'annonce</Link>}
                        </div>
                        <div className="text-right flex flex-col items-end gap-2">
                          <p className={`text-xl font-bold ${order.current_status === 'cancelled' || order.current_status === 'refunded' || order.current_status === 'rejected' ? 'text-red-400 line-through' : 'text-foreground'}`}>{formatPrice(order.total)}</p>
                          {order.current_status === 'pending_payment' && (
                            <div className="space-y-1">
                              <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={async () => {
                                try {
                                  const res = await axios.post(`${API}/payments/proceed-to-payment/${order.id}`, { origin_url: window.location.origin }, { headers: { Authorization: `Bearer ${token}` } });
                                  if (res.data.checkout_url) { window.location.href = res.data.checkout_url; }
                                  else { toast.success('Consultez les instructions de paiement.'); setViewingOrderDetail({ ...order, payment_instructions: res.data.payment_instructions, payment_deadline: res.data.payment_deadline }); }
                                } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
                              }} data-testid={`pay-now-${order.id}`}><CreditCard className="w-3.5 h-3.5 mr-1" />Payer</Button>
                              {order.payment_deadline && <CountdownTimer deadline={order.payment_deadline} />}
                            </div>
                          )}
                          {order.current_status === 'rejected' && order.rejection_reason && <p className="text-xs text-red-400 max-w-[200px] text-right">Raison: {order.rejection_reason}</p>}
                          {order.current_status === 'pending_admin_validation' && <p className="text-xs text-amber-400">En cours de vérification...</p>}
                          <Button variant="outline" size="sm" className="bg-muted/40 border-border text-muted-foreground" onClick={() => setViewingOrderDetail(order)} data-testid={`view-all-detail-${order.id}`}><Eye className="w-3.5 h-3.5 mr-1" />Détails</Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* ── TRUST SECTION ── */}
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="trust-section">
          <div className="bg-muted/30 border border-border rounded-xl p-5 text-center">
            <ShieldCheck className="w-8 h-8 text-[#D4AF37] mx-auto mb-3" />
            <h3 className="font-semibold text-foreground text-sm mb-1">Paiements sécurisés</h3>
            <p className="text-xs text-muted-foreground">Fonds en séquestre jusqu'à la confirmation de livraison</p>
          </div>
          <div className="bg-muted/30 border border-border rounded-xl p-5 text-center">
            <Shield className="w-8 h-8 text-emerald-400 mx-auto mb-3" />
            <h3 className="font-semibold text-foreground text-sm mb-1">Vendeurs vérifiés</h3>
            <p className="text-xs text-muted-foreground">Chaque vendeur est vérifié par notre équipe avant activation</p>
          </div>
          <div className="bg-muted/30 border border-border rounded-xl p-5 text-center">
            <Headphones className="w-8 h-8 text-violet-400 mx-auto mb-3" />
            <h3 className="font-semibold text-foreground text-sm mb-1">Support réactif</h3>
            <p className="text-xs text-muted-foreground">Assistance par ticket 7j/7 pour tous vos envois</p>
          </div>
        </div>

        {/* ── DIALOGS (unchanged logic) ── */}
        <Dialog open={disputeDialog.open} onOpenChange={(open) => setDisputeDialog({ ...disputeDialog, open })}>
          <DialogContent>
            <DialogHeader><DialogTitle>Ouvrir un litige</DialogTitle><DialogDescription>Décrivez le problème rencontré</DialogDescription></DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium">Raison</label>
                <Select value={disputeForm.reason} onValueChange={(v) => setDisputeForm({ ...disputeForm, reason: v })}>
                  <SelectTrigger><SelectValue placeholder="Sélectionnez" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not_received">Colis non reçu</SelectItem>
                    <SelectItem value="damaged">Colis endommagé</SelectItem>
                    <SelectItem value="wrong_item">Mauvais article</SelectItem>
                    <SelectItem value="other">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Description</label>
                <Textarea placeholder="Décrivez le problème en détail..." value={disputeForm.description} onChange={(e) => setDisputeForm({ ...disputeForm, description: e.target.value })} rows={4} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDisputeDialog({ open: false, orderId: null })}>Annuler</Button>
              <Button onClick={submitDispute} disabled={!disputeForm.reason || !disputeForm.description} className="bg-red-500 hover:bg-red-600">Ouvrir le litige</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <OrderDetailModal order={viewingOrderDetail} open={!!viewingOrderDetail} onClose={() => setViewingOrderDetail(null)} formatPrice={formatPrice} />

        <Dialog open={cancelDialog.open} onOpenChange={(open) => !open && setCancelDialog({ open: false, order: null, fee: 0, feeRate: 0, loading: false })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-500"><AlertTriangle className="w-5 h-5" />Annuler la commande</DialogTitle>
              <DialogDescription>Êtes-vous sûr de vouloir annuler cette commande ?</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {cancelDialog.order && (
                <div className="bg-muted rounded-lg p-4 space-y-2 text-sm">
                  <p><strong>Commande :</strong> #{cancelDialog.order.id?.slice(0, 8).toUpperCase()}</p>
                  <p><strong>Trajet :</strong> {flag(cancelDialog.order.departure_country)} {cancelDialog.order.departure_city} → {flag(cancelDialog.order.destination_country)} {cancelDialog.order.destination_city}</p>
                  <p><strong>Montant total :</strong> {formatPrice(cancelDialog.order.total)}</p>
                </div>
              )}
              {cancelDialog.feeRate > 0 ? (
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 space-y-2">
                  <p className="font-semibold text-red-600">Frais d'annulation applicables</p>
                  <p className="text-sm text-red-500">{cancelDialog.feeRate === 50 ? "Annulation à moins de 2 jours du voyage : 50% du coût total sera facturé." : "Annulation à moins de 1 semaine du voyage : 25% du coût total sera facturé."}</p>
                  <div className="flex justify-between font-bold text-red-600 pt-2 border-t border-red-500/20"><span>Frais ({cancelDialog.feeRate}%)</span><span>{formatPrice(cancelDialog.fee)}</span></div>
                </div>
              ) : (
                <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
                  <p className="text-sm text-green-600">{cancelDialog.order?.current_status === 'pending_admin_validation' ? "Aucun frais d'annulation ne sera appliqué car la commande n'a pas encore été validée." : "Aucun frais d'annulation applicable (plus de 7 jours avant le voyage)."}</p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCancelDialog({ open: false, order: null, fee: 0, feeRate: 0, loading: false })}>Retour</Button>
              <Button variant="destructive" onClick={confirmCancel} disabled={cancelDialog.loading} data-testid="confirm-cancel-order-btn">{cancelDialog.loading ? 'Annulation...' : 'Accepter et annuler'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <ModifyOrderDialog open={modifyDialog.open} order={modifyDialog.order} onClose={() => setModifyDialog({ open: false, order: null, loading: false })} API={API} token={token} formatPrice={formatPrice} onSuccess={() => { setModifyDialog({ open: false, order: null, loading: false }); fetchData(); }} />
      </div>
    </div>
  );
};

// ─── MODIFY ORDER DIALOG (preserved) ────────────────────────────────
const ModifyOrderDialog = ({ open, order, onClose, API, token, formatPrice, onSuccess }) => {
  const [description, setDescription] = React.useState('');
  const [instructions, setInstructions] = React.useState('');
  const [declaredValue, setDeclaredValue] = React.useState(0);
  const [declaredWeight, setDeclaredWeight] = React.useState(0);
  const [insuranceOptIn, setInsuranceOptIn] = React.useState(false);
  const [selectedBaggage, setSelectedBaggage] = React.useState({});
  const [listing, setListing] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [insuranceInfo, setInsuranceInfo] = React.useState(null);
  const BAGGAGE_LABELS = { kilos: 'Kilos', sacs: 'Sacs', enveloppes: 'Enveloppes', sac_23kg: 'Sacs 23kg', telephones: 'Téléphones', ordinateurs: 'Ordinateurs' };

  React.useEffect(() => {
    if (order && open) {
      setDescription(order.article_description || '');
      setInstructions(order.delivery_instructions || '');
      setDeclaredValue(order.declared_value || 0);
      setDeclaredWeight(order.declared_weight || 0);
      setInsuranceOptIn(order.insurance_opt_in || false);
      const bagMap = {};
      (order.selected_items || []).forEach(item => { bagMap[item.type] = item.quantity; });
      setSelectedBaggage(bagMap);
      const fetchListing = async () => { try { const res = await axios.get(`${API}/listings/${order.listing_id}`, { headers: { Authorization: `Bearer ${token}` } }); setListing(res.data); } catch (err) { console.error(err); } };
      const fetchInsurance = async () => { try { const res = await axios.get(`${API}/insurance/info`, { headers: { Authorization: `Bearer ${token}` } }); setInsuranceInfo(res.data); } catch (err) { /* */ } };
      fetchListing();
      fetchInsurance();
    }
  }, [order, open, API, token]);

  const calculateTotal = () => {
    let subtotal = 0;
    if (listing?.baggage_items) { listing.baggage_items.forEach(item => { subtotal += (selectedBaggage[item.type] || 0) * item.price_per_unit; }); }
    const commission = subtotal * 0.1;
    let insurance = 0;
    if (insuranceInfo && declaredValue > 0 && insuranceOptIn) { const base = insuranceInfo.base_coverage || 100; if (declaredValue > base) insurance = (declaredValue - base) * (insuranceInfo.rate || 0.02); }
    return { subtotal, commission, insurance, total: subtotal + commission + insurance };
  };

  const totalQty = Object.values(selectedBaggage).reduce((a, b) => a + b, 0);

  const handleSave = async () => {
    if (totalQty === 0) { toast.error('Sélectionnez au moins un type de bagage'); return; }
    setLoading(true);
    try {
      const items = Object.entries(selectedBaggage).filter(([, qty]) => qty > 0).map(([type, quantity]) => ({ type, quantity }));
      await axios.put(`${API}/orders/${order.id}/buyer-modify`, { article_description: description, delivery_instructions: instructions, selected_items: items, declared_value: declaredValue, declared_weight: declaredWeight, insurance_opt_in: insuranceOptIn }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Commande modifiée avec succès');
      onSuccess();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur lors de la modification'); } finally { setLoading(false); }
  };

  if (!order) return null;
  const totals = calculateTotal();

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Pencil className="w-5 h-5 text-[#D4AF37]" />Modifier la commande</DialogTitle>
          <DialogDescription>#{order.id?.slice(0, 8).toUpperCase()} - {flag(order.departure_country)} {order.departure_city} → {flag(order.destination_country)} {order.destination_city}</DialogDescription>
        </DialogHeader>
        <div className="space-y-5 py-2">
          {listing?.baggage_items && listing.baggage_items.length > 0 && (
            <div>
              <label className="text-sm font-medium mb-2 block">Types de bagages</label>
              <div className="space-y-2">
                {listing.baggage_items.map(item => {
                  const currentOrderQty = (order.selected_items || []).find(s => s.type === item.type)?.quantity || 0;
                  const maxAvailable = (item.available_quantity || 0) + currentOrderQty;
                  const qty = selectedBaggage[item.type] || 0;
                  return (
                    <div key={item.type} className="flex items-center justify-between bg-muted/50 rounded-lg p-3">
                      <div><p className="text-sm font-medium">{BAGGAGE_LABELS[item.type] || item.type}</p><p className="text-xs text-muted-foreground">{formatPrice(item.price_per_unit)}/unité - Dispo: {maxAvailable}</p></div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => setSelectedBaggage(p => ({...p, [item.type]: Math.max(0, (p[item.type] || 0) - 1)}))} disabled={qty === 0} data-testid={`modify-dec-${item.type}`}>-</Button>
                        <span className="w-8 text-center text-sm font-medium">{qty}</span>
                        <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => setSelectedBaggage(p => ({...p, [item.type]: Math.min(maxAvailable, (p[item.type] || 0) + 1)}))} disabled={qty >= maxAvailable} data-testid={`modify-inc-${item.type}`}>+</Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <div><label className="text-sm font-medium">Description des articles</label><Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Décrivez les articles..." rows={2} className="mt-1" data-testid="modify-description" /></div>
          <div><label className="text-sm font-medium">Consignes de livraison</label><Textarea value={instructions} onChange={(e) => setInstructions(e.target.value)} placeholder="Nom destinataire, téléphone..." rows={2} className="mt-1" data-testid="modify-instructions" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="text-sm font-medium">Poids déclaré (kg)</label><Input type="number" min={0} step={0.1} value={declaredWeight || ''} onChange={(e) => setDeclaredWeight(parseFloat(e.target.value) || 0)} className="mt-1" data-testid="modify-weight" /></div>
            <div><label className="text-sm font-medium">Valeur déclarée (CAD)</label><Input type="number" min={0} step={1} value={declaredValue || ''} onChange={(e) => setDeclaredValue(parseFloat(e.target.value) || 0)} className="mt-1" data-testid="modify-value" /></div>
          </div>
          {insuranceInfo && declaredValue > (insuranceInfo.base_coverage || 100) && (
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-3">
              <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={insuranceOptIn} onChange={(e) => setInsuranceOptIn(e.target.checked)} className="rounded" data-testid="modify-insurance-checkbox" /><span className="text-sm">Assurance complète (+{((declaredValue - (insuranceInfo.base_coverage || 100)) * (insuranceInfo.rate || 0.02)).toFixed(2)} CAD)</span></label>
              <p className="text-xs text-muted-foreground mt-1">Couverture gratuite jusqu'à {insuranceInfo.base_coverage || 100} CAD</p>
            </div>
          )}
          <div className="bg-muted rounded-lg p-4 space-y-2 text-sm">
            <div className="flex justify-between"><span>Sous-total</span><span>{formatPrice(totals.subtotal)}</span></div>
            <div className="flex justify-between text-muted-foreground"><span>Commission (10%)</span><span>{formatPrice(totals.commission)}</span></div>
            {totals.insurance > 0 && <div className="flex justify-between text-primary"><span>Assurance</span><span>{formatPrice(totals.insurance)}</span></div>}
            <div className="flex justify-between font-bold border-t pt-2"><span>Total</span><span className="text-primary">{formatPrice(totals.total)}</span></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={handleSave} disabled={loading || totalQty === 0} data-testid="confirm-modify-btn">{loading ? 'Enregistrement...' : 'Enregistrer les modifications'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BuyerDashboard;
